// Enhances Avis textareas to enforce optional per-quantity line counts without modifying Avis markup.
if (!window.avisEnrichmentsLoaded) {
    window.avisEnrichmentsLoaded = true;

    (function () {
        const CONTAINER_SELECTOR = '.process-split-lines';
        const LINE_JOINER = ' ¶ ';
        const ERROR_CLASS = 'avis-enrichment__error';
        const formEntries = new Map();

        function onReady(fn) {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', fn);
            } else {
                fn();
            }
        }

        function isVisible(el) {
            return !!(el && (el.offsetParent || el.getClientRects().length));
        }

        function parseLines(textarea, preserveEmpty = false) {
            const lines = textarea.value
                .split(/\r?\n/)
                .map(line => line.trim());
            return preserveEmpty ? lines : lines.filter(line => line.length > 0);
        }

        function ensureErrorElement(container) {
            let el = container.querySelector('.' + ERROR_CLASS);
            if (!el) {
                el = document.createElement('div');
                el.className = ERROR_CLASS;
                el.style.color = 'var(--color-error, #d72c0d)';
                el.style.fontSize = '0.875rem';
                el.style.marginTop = '0.35rem';
                el.style.display = 'none';
                container.appendChild(el);
            }
            return el;
        }

        function showError(el, message) {
            if (!el) return;
            el.textContent = message;
            el.style.display = message ? 'block' : 'none';
        }

        function clearError(el) {
            if (!el) return;
            el.textContent = '';
            el.style.display = 'none';
        }

        function findQuantityInput(form, container) {
            const selectors = ['input[name="quantity"]', 'input[id^="Quantity-"]', '#Quantity'];
            const scopes = [];

            if (form) scopes.push(form);
            const productWrap = container ? container.closest('.product') : null;
            if (productWrap) scopes.push(productWrap);
            scopes.push(document);

            for (const scope of scopes) {
                for (const selector of selectors) {
                    const el = scope.querySelector(selector);
                    if (el) return el;
                }
            }
            return null;
        }

        function getQuantityValue(input) {
            if (!input) return null;
            const value = parseInt(input.value, 10);
            if (!Number.isFinite(value) || value < 1) return null;
            return value;
        }

        function getFormEntry(form, container) {
            const key = form || document;
            let entry = formEntries.get(key);

            if (!entry) {
                entry = {
                    form: form || null,
                    containers: new Set(),
                    quantityInput: findQuantityInput(form, container),
                    quantityListenerAttached: false,
                    submitBound: false
                };
                formEntries.set(key, entry);
            } else if (!entry.quantityInput) {
                entry.quantityInput = findQuantityInput(form, container);
            }

            attachQuantityListener(entry);
            attachSubmitListener(entry);

            return entry;
        }

        function attachQuantityListener(entry) {
            if (!entry.quantityInput || entry.quantityListenerAttached) return;
            const handler = () => {
                entry.containers.forEach(containerEntry => validateContainer(containerEntry));
            };
            entry.quantityInput.addEventListener('input', handler);
            entry.quantityInput.addEventListener('change', handler);
            entry.quantityListenerAttached = true;
        }

        function attachSubmitListener(entry) {
            if (!entry.form || entry.submitBound) return;
            entry.form.addEventListener('submit', event => {
                let firstInvalid = null;
                entry.containers.forEach(containerEntry => {
                    const ok = validateContainer(containerEntry);
                    if (!ok && !firstInvalid) {
                        firstInvalid = containerEntry;
                    }
                });

                if (firstInvalid) {
                    event.preventDefault();
                    event.stopPropagation();
                    firstInvalid.textarea.focus();
                    if (typeof firstInvalid.textarea.reportValidity === 'function') {
                        firstInvalid.textarea.reportValidity();
                    }
                    return;
                }

                prepareSubmission(entry);
            });
            entry.submitBound = true;
        }

        function validateContainer(containerEntry) {
            const { container, textarea, requiresEqualQuantity, errorEl, formEntry } = containerEntry;

            // Do not block submission when the control is hidden (single-name mode, etc.)
            if (!isVisible(container)) {
                textarea.setCustomValidity('');
                clearError(errorEl);
                return true;
            }

            const lines = parseLines(textarea, true);
            let message = '';

            if (requiresEqualQuantity) {
                const qty = getQuantityValue(formEntry.quantityInput);
                if (qty && lines.length !== qty) {
                    message = `Please enter exactly ${qty} name${qty === 1 ? '' : 's'}, one per line.`;
                } else if (lines.some(line => line.length < 1 || line.length > 20)) {
                    message = 'Each name must be between 1 and 20 characters.';
                }
            }

            if (message) {
                textarea.setCustomValidity(message);
                showError(errorEl, message);
                return false;
            }

            textarea.setCustomValidity('');
            clearError(errorEl);
            return true;
        }

        function ensureHiddenField(containerEntry) {
            if (containerEntry.hiddenField) return containerEntry.hiddenField;

            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            const baseName = containerEntry.textarea.name || containerEntry.textarea.id || 'avis_lines';
            hidden.name = `properties[${baseName}-separated]`;

            // Place inside the owning form when possible so Shopify captures it
            const targetForm = containerEntry.formEntry.form || containerEntry.textarea.form;
            if (targetForm) {
                targetForm.appendChild(hidden);
            } else {
                containerEntry.container.appendChild(hidden);
            }
            containerEntry.hiddenField = hidden;
            return hidden;
        }

        function prepareSubmission(entry) {
            entry.containers.forEach(containerEntry => {
                const { container, textarea } = containerEntry;

                if (!isVisible(container)) return;

                const lines = parseLines(textarea, true);
                const hidden = ensureHiddenField(containerEntry);
                hidden.value = lines.length ? lines.join(LINE_JOINER) : '';
            });
        }

        function initContainer(container) {
            if (!container || container.dataset.avisEnriched === 'true') return;

            const textarea = container.querySelector('textarea');
            if (!textarea) return;

            const form = textarea.form || container.closest('form') || null;
            const formEntry = getFormEntry(form, container);
            const errorEl = ensureErrorElement(container);

            const containerEntry = {
                container,
                textarea,
                requiresEqualQuantity: container.classList.contains('lines-equal-quantity'),
                errorEl,
                formEntry
            };

            formEntry.containers.add(containerEntry);
            container.dataset.avisEnriched = 'true';

            textarea.addEventListener('input', () => {
                validateContainer(containerEntry);
                prepareSubmission(formEntry);
            });
            textarea.addEventListener('change', () => {
                validateContainer(containerEntry);
                prepareSubmission(formEntry);
            });

            validateContainer(containerEntry);
        }

        function scanExisting() {
            document.querySelectorAll(CONTAINER_SELECTOR).forEach(initContainer);
        }

        function setupObserver() {
            const observer = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    mutation.addedNodes.forEach(node => {
                        if (!(node instanceof HTMLElement)) return;

                        if (node.matches(CONTAINER_SELECTOR)) {
                            initContainer(node);
                        } else {
                            node.querySelectorAll(CONTAINER_SELECTOR).forEach(initContainer);
                        }
                    });
                });
            });

            observer.observe(document.body, { childList: true, subtree: true });
        }

        onReady(() => {
            scanExisting();
            setupObserver();
        });
    })();
}
